import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { PreviewSessionStatus, PreviewSessionEntry } from '../types';

export interface UsePreviewSessionManagerReturn {
  sessions: PreviewSessionEntry[];
  addSession: (session: Omit<PreviewSessionEntry, 'status'>) => void;
  removeSession: (deviceId: string) => void;
  updateSessionStatus: (deviceId: string, status: PreviewSessionStatus) => void;
  getSessionsForCustomer: (customerId: string) => PreviewSessionEntry[];
  getOtherSessionForCustomerByDevice: (deviceId: string) => PreviewSessionEntry[];
  getSession: (deviceId: string) => PreviewSessionEntry | undefined;
}

const EXPIRY_CHECK_INTERVAL_MS = 10_000;

export const usePreviewSessionManager = (): UsePreviewSessionManagerReturn => {
  const [sessionMap, setSessionMap] = useState<Map<string, PreviewSessionEntry>>(new Map());
  const sessionMapRef = useRef(sessionMap);

  useEffect(() => {
    sessionMapRef.current = sessionMap;
  }, [sessionMap]);

  const sessions = useMemo(() => Array.from(sessionMap.values()), [sessionMap]);

  const addSession = useCallback((session: Omit<PreviewSessionEntry, 'status'>) => {
    setSessionMap((prev) =>
      new Map(prev).set(session.deviceId, { ...session, status: PreviewSessionStatus.ACTIVE }),
    );
  }, []);

  const removeSession = useCallback((deviceId: string) => {
    setSessionMap((prev) => {
      if (!prev.has(deviceId)) return prev;
      const next = new Map(prev);
      next.delete(deviceId);
      return next;
    });
  }, []);

  const updateSessionStatus = useCallback((deviceId: string, status: PreviewSessionStatus) => {
    setSessionMap((prev) => {
      const session = prev.get(deviceId);
      if (!session) return prev;
      return new Map(prev).set(deviceId, { ...session, status });
    });
  }, []);

  const getSessionsForCustomer = useCallback((customerId: string): PreviewSessionEntry[] => {
    return Array.from(sessionMapRef.current.values()).filter((s) => s.customerId === customerId);
  }, []);

  const getOtherSessionForCustomerByDevice = useCallback(
    (deviceId: string): PreviewSessionEntry[] => {
      const session = sessionMapRef.current.get(deviceId);
      if (!session || !session.customerId) return [];
      return Array.from(sessionMapRef.current.values()).filter(
        (s) => s.customerId === session.customerId && s.deviceId !== deviceId,
      );
    },
    [],
  );

  const getSession = useCallback((deviceId: string): PreviewSessionEntry | undefined => {
    return sessionMapRef.current.get(deviceId);
  }, []);

  // Auto-expiry: remove expired sessions every 10s (only ACTIVE ones)
  useEffect(() => {
    const timer = setInterval(() => {
      setSessionMap((prev) => {
        const now = Date.now();
        const expired = [...prev.entries()].filter(
          ([, s]) => s.status === PreviewSessionStatus.ACTIVE && s.expiresAt <= now,
        );
        if (expired.length === 0) return prev;

        const next = new Map(prev);
        for (const [deviceId] of expired) next.delete(deviceId);
        return next;
      });
    }, EXPIRY_CHECK_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);

  return useMemo(
    () => ({
      sessions,
      addSession,
      removeSession,
      updateSessionStatus,
      getSessionsForCustomer,
      getOtherSessionForCustomerByDevice,
      getSession,
    }),
    [
      sessions,
      addSession,
      removeSession,
      updateSessionStatus,
      getSessionsForCustomer,
      getOtherSessionForCustomerByDevice,
      getSession,
    ],
  );
};
